import { supabase } from '../lib/supabase';

interface PageViewData {
  page_path: string;
  page_title: string;
  referrer: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

interface AnalyticsEvent {
  event_type: string;
  event_category?: string;
  event_label?: string;
  event_value?: number;
  metadata?: Record<string, any>;
}

class AnalyticsService {
  private sessionId: string;
  private visitorId: string;
  private currentPageStart: number;
  private deviceInfo: any;

  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.visitorId = this.getOrCreateVisitorId();
    this.currentPageStart = Date.now();
    this.deviceInfo = this.getDeviceInfo();

    this.setupBeforeUnload();
  }

  private getOrCreateSessionId(): string {
    const SESSION_KEY = 'analytics_session_id';
    const SESSION_DURATION = 30 * 60 * 1000;

    const stored = sessionStorage.getItem(SESSION_KEY);
    const timestamp = sessionStorage.getItem('analytics_session_timestamp');

    if (stored && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age < SESSION_DURATION) {
        sessionStorage.setItem('analytics_session_timestamp', Date.now().toString());
        return stored;
      }
    }

    const newSessionId = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, newSessionId);
    sessionStorage.setItem('analytics_session_timestamp', Date.now().toString());
    return newSessionId;
  }

  private getOrCreateVisitorId(): string {
    const VISITOR_KEY = 'analytics_visitor_id';

    let visitorId = localStorage.getItem(VISITOR_KEY);
    if (!visitorId) {
      visitorId = this.generateFingerprint();
      localStorage.setItem(VISITOR_KEY, visitorId);
    }

    return visitorId;
  }

  private generateFingerprint(): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('fingerprint', 2, 2);
    }

    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.colorDepth,
      screen.width,
      screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL()
    ].join('|');

    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    return 'v_' + Math.abs(hash).toString(36);
  }

  private getDeviceInfo() {
    const ua = navigator.userAgent;

    let device_type = 'desktop';
    if (/Mobile|Android|iPhone/i.test(ua)) device_type = 'mobile';
    else if (/iPad|Tablet/i.test(ua)) device_type = 'tablet';

    let browser = 'Unknown';
    if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';

    let os = 'Unknown';
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS')) os = 'iOS';

    return { device_type, browser, os };
  }

  private parseUTMParams(): Record<string, string> {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_term: params.get('utm_term') || '',
      utm_content: params.get('utm_content') || ''
    };
  }

  private setupBeforeUnload() {
    window.addEventListener('beforeunload', () => {
      this.trackPageDuration();
    });
  }

  private async trackPageDuration() {
    const duration = Math.round((Date.now() - this.currentPageStart) / 1000);

    if (duration > 0) {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          return;
        }

        await supabase.rpc('update_page_duration', {
          session_id: this.sessionId,
          duration_seconds: duration
        });
      } catch (error) {
        console.warn('Could not track page duration:', error);
      }
    }
  }

  private async getGeolocation(): Promise<{
    country: string | null;
    country_code: string | null;
    region: string | null;
    city: string | null;
    latitude: number | null;
    longitude: number | null;
  }> {
    // DISABLED: ipapi.co has rate limits and CORS issues
    // Geolocation is non-critical for core functionality
    // TODO: Consider server-side geolocation lookup using Cloudflare headers or similar
    return {
      country: null,
      country_code: null,
      region: null,
      city: null,
      latitude: null,
      longitude: null
    };
  }

  async trackPageView(data?: Partial<PageViewData>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const utmParams = this.parseUTMParams();
      const geo = await this.getGeolocation();

      const pageViewData = {
        session_id: this.sessionId,
        visitor_id: this.visitorId,
        user_id: user?.id || null,
        page_path: data?.page_path || window.location.pathname,
        page_title: data?.page_title || document.title,
        referrer: data?.referrer || document.referrer,
        utm_source: data?.utm_source || utmParams.utm_source || null,
        utm_medium: data?.utm_medium || utmParams.utm_medium || null,
        utm_campaign: data?.utm_campaign || utmParams.utm_campaign || null,
        utm_term: data?.utm_term || utmParams.utm_term || null,
        utm_content: data?.utm_content || utmParams.utm_content || null,
        user_agent: navigator.userAgent,
        device_type: this.deviceInfo.device_type,
        browser: this.deviceInfo.browser,
        os: this.deviceInfo.os,
        country: geo.country,
        country_code: geo.country_code,
        region: geo.region,
        city: geo.city,
        latitude: geo.latitude,
        longitude: geo.longitude
      };

      // All analytics DB operations are wrapped in try-catch to prevent blocking auth flows
      try {
        await supabase.from('page_views').insert(pageViewData);
      } catch (e) {
        // Silently fail - analytics should never block core functionality
      }

      try {
        await this.updateSession(pageViewData);
      } catch (e) {
        // Silently fail - session tracking is non-critical
      }

      if (document.referrer) {
        try {
          await this.trackTrafficSource(document.referrer);
        } catch (e) {
          // Silently fail - traffic source tracking is non-critical
        }
      }

      this.currentPageStart = Date.now();
    } catch (error) {
      // Top-level catch for any unexpected errors
      // Analytics failures should never affect user experience
    }
  }

  private async updateSession(pageViewData: any) {
    try {
      // First try to get existing session to calculate page_count
      const { data: existingSession } = await supabase
        .from('visitor_sessions')
        .select('page_count')
        .eq('id', this.sessionId)
        .maybeSingle();

      const pageCount = existingSession ? (existingSession.page_count || 0) + 1 : 1;

      // Use upsert to avoid race conditions from React strict mode double-render
      const { error } = await supabase
        .from('visitor_sessions')
        .upsert({
          id: this.sessionId,
          visitor_id: this.visitorId,
          user_id: pageViewData.user_id,
          session_start: existingSession ? undefined : new Date().toISOString(),
          session_end: new Date().toISOString(),
          entry_page: existingSession ? undefined : pageViewData.page_path,
          exit_page: pageViewData.page_path,
          referrer_domain: this.extractDomain(pageViewData.referrer),
          utm_source: pageViewData.utm_source,
          utm_medium: pageViewData.utm_medium,
          utm_campaign: pageViewData.utm_campaign,
          device_type: pageViewData.device_type,
          country: pageViewData.country,
          region: pageViewData.region,
          page_count: pageCount
        }, {
          onConflict: 'id',
          ignoreDuplicates: false
        });

      if (error) {
        console.warn('Session upsert error (non-critical):', error.message);
      }
    } catch (error) {
      console.warn('Session update error (non-critical):', error);
    }
  }

  private extractDomain(url: string): string | null {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return null;
    }
  }

  private async trackTrafficSource(referrer: string) {
    try {
      const domain = this.extractDomain(referrer);
      if (!domain || domain === window.location.hostname) return;

      const sourceType = this.categorizeSource(domain);

      await supabase.from('traffic_sources').insert({
        session_id: this.sessionId,
        source_type: sourceType,
        source_name: domain,
        referrer_url: referrer,
        referrer_domain: domain,
        landing_page: window.location.pathname
      });

      const { data: existingBacklink } = await supabase
        .from('backlinks_discovered')
        .select('total_visits')
        .eq('referrer_domain', domain)
        .maybeSingle();

      const totalVisits = existingBacklink ? (existingBacklink.total_visits || 0) + 1 : 1;

      // Use upsert to avoid race conditions
      const { error } = await supabase
        .from('backlinks_discovered')
        .upsert({
          referrer_domain: domain,
          last_seen: new Date().toISOString(),
          total_visits: totalVisits
        }, {
          onConflict: 'referrer_domain',
          ignoreDuplicates: false
        });

      if (error) {
        console.warn('Backlink upsert error (non-critical):', error.message);
      }
    } catch (error) {
      console.warn('Traffic source tracking error (non-critical):', error);
    }
  }

  private categorizeSource(domain: string): string {
    const socialDomains = [
      'facebook.com',
      'fb.com',
      'l.facebook.com',
      'm.facebook.com',
      'lm.facebook.com',
      'twitter.com',
      'x.com',
      't.co',
      'linkedin.com',
      'instagram.com',
      'tiktok.com',
      'youtube.com',
      'youtu.be'
    ];
    const searchEngines = ['google.com', 'bing.com', 'yahoo.com', 'duckduckgo.com', 'baidu.com'];

    if (socialDomains.some(d => domain.includes(d))) return 'social';
    if (searchEngines.some(d => domain.includes(d))) return 'organic';

    return 'referral';
  }

  async trackEvent(event: AnalyticsEvent) {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      await supabase.from('analytics_events').insert({
        session_id: this.sessionId,
        user_id: user?.id || null,
        event_type: event.event_type,
        event_category: event.event_category,
        event_label: event.event_label,
        event_value: event.event_value,
        page_path: window.location.pathname,
        metadata: event.metadata || {}
      });
    } catch (error) {
      console.error('Event tracking error:', error);
    }
  }

  async trackConversion(type: string, value: number = 0) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: session } = await supabase
        .from('visitor_sessions')
        .select('session_start')
        .eq('id', this.sessionId)
        .single();

      let timeToConversion = 0;
      if (session?.session_start) {
        const start = new Date(session.session_start).getTime();
        timeToConversion = (Date.now() - start) / (1000 * 60 * 60);
      }

      await supabase.from('conversion_events').insert({
        session_id: this.sessionId,
        user_id: user.id,
        conversion_type: type,
        conversion_value: value,
        time_to_conversion_hours: timeToConversion,
        touchpoints_count: 1
      });

      const { data: trafficSource } = await supabase
        .from('traffic_sources')
        .select('referrer_domain')
        .eq('session_id', this.sessionId)
        .maybeSingle();

      if (trafficSource?.referrer_domain) {
        await supabase.rpc('increment_backlink_conversions', {
          domain: trafficSource.referrer_domain
        });
      }
    } catch (error) {
      console.error('Conversion tracking error:', error);
    }
  }

  async trackPerformance(metrics: {
    page_path: string;
    load_time_ms?: number;
    ttfb_ms?: number;
    dom_ready_ms?: number;
    fcp_ms?: number;
    lcp_ms?: number;
  }) {
    try {
      await supabase.from('page_performance').insert({
        ...metrics,
        device_type: this.deviceInfo.device_type
      });
    } catch (error) {
      console.error('Performance tracking error:', error);
    }
  }
}

let analyticsInstance: AnalyticsService | null = null;

function getAnalyticsService(): AnalyticsService {
  if (typeof window === 'undefined') {
    throw new Error('Analytics service can only be used in browser environment');
  }

  if (!analyticsInstance) {
    analyticsInstance = new AnalyticsService();
  }

  return analyticsInstance;
}

export const analyticsService = new Proxy({} as AnalyticsService, {
  get(target, prop) {
    const service = getAnalyticsService();
    const value = service[prop as keyof AnalyticsService];

    if (typeof value === 'function') {
      return value.bind(service);
    }

    return value;
  }
});
